import api from "@/api";
import dayjs from "dayjs";
import * as types from "./mutation-types";
import { initialState, mockState } from "./state";

export const actions = {
  reset({ commit }) {
    commit("reset");
  },

  /**
   * Submit a survey
   */

  async submitResponse({ state }, payload) {
    let response;
    const { id } = state.currentSurvey;

    try {
      response = await api().post(`/surveymanagement/survey/${id}/entry`, {
        entry: { data: JSON.stringify(payload) },
      });
      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(new Error(err));
    }
  },

  /**
   * Get submissions
   */
  async getEntries({ commit, rootState }, params) {
    let response;
    let filter = {};

    if (params && params.currentUser) {
      filter = {
        filter: {
          query: {
            bool: {
              must: {
                term: {
                  user_id: rootState.auth.currentUser.id,
                },
              },
            },
          },
        },
      };
    }

    try {
      if (params.currentUser) {
        response = await api().put(
          `/surveymanagement/survey/${params.surveyId}/entries`,
          filter
        );
      } else {
        response = await api().get(
          `/surveymanagement/survey/${params.surveyId}/entries`
        );
      }
      commit("SET_SURVEY_ENTRIES", response.data.data.entry);
      return Promise.resolve(response.data.data.entry);
    } catch (err) {
      commit("SET_SURVEY_ENTRIES", []);
      return Promise.resolve([]);
    }
  },

  /**
   * Generate a CSV file for the current form
   */
   generateCsv({ getters, state }, entries) {
    if (entries) {
      const csvData = [];

      const results = entries;
      const questions = [];
      const ids = [];

      // have to go through each answer
      results.forEach((res) => {
        res.answers.forEach((ans) => {
          const obj = {
            id: ans.id,
            value: ans.question,
          };
          if (!ids.includes(ans.origId)) {
            questions.push(obj);
            ids.push(ans.origId);
          }
        });
      });

      const titles = questions.sort((a, b) => a.id - b.id);

      const submissions = entries;
      const formattedTitles = titles.map((title) => title.value);

      // titles
      if (!state.currentSurvey.anonymous) {
        csvData.push(["Date Submitted", "Submitted By", ...formattedTitles]);
      } else {
        csvData.push(["Date Submitted", ...formattedTitles]);
      }

      // responses
      let formattedAnswers = [];
      submissions.forEach((entry) => {
        formattedAnswers = entry.answers.map((ans) => {
          if (!ans.isFile || ans.answer == "N/A") {
            return `"${ans.answer}"`;
          }
          const images = `${ans.answer}`.split(",");
          let returnArr = [];
          images.forEach((img) => {
            returnArr.push(
              `${window.location.protocol}//${window.location.hostname}/admin/file/${img}`
            );
          });
          return `"${returnArr.join(", ")}"`;
        });
        if (!state.currentSurvey.anonymous) {
          csvData.push([
            `${entry.dateSubmitted}`,
            `${entry.user ? entry.user.name : "N/A"} (${
              entry.user ? entry.user.email : "N/A"
            })`,
            ...formattedAnswers,
          ]);
        } else {
          csvData.push([`${entry.dateSubmitted}`, ...formattedAnswers]);
        }
      });

      const csvContent = csvData.map((e) => e.join(",")).join("\n");

      return Promise.resolve(csvContent);
    }

    return false;
  },

  // FORM CRUD ACTIONS

  /**
   * Pending and rejected need to go through versions, not documents
   */
  list({ commit, state }, options = null) {
    commit("setSurveys", []);
    state.loading = true;
    let page = options && options.page ? options.page : 1;
    let flat = options && options.flat;

    const { tabFilter } = state;

    let listFavourites = false;

    const size = state.pagination.per_page;
    let from = size * page - size;

    if (!from) {
      from = 0;
    }

    const statusFilter =
      tabFilter && tabFilter !== "starred" ? tabFilter : "approved";

    const filter = {
      sort: [
        {
          [state.sortBy.field]: {
            order: state.sortBy.direction,
          },
        },
      ],
      size,
      from,
      query: {
        bool: {
          must: [
            {
              bool: {
                should: [
                  {
                    bool: {
                      should: [
                        {
                          match: {
                            survey_title: {
                              query: state.filters.searchQuery,
                              operator: "and",
                              zero_terms_query: "all",
                              fuzziness: "AUTO",
                            },
                          },
                        },
                        {
                          match_phrase_prefix: {
                            survey_title: {
                              query: state.filters.searchQuery,
                              slop: 50,
                            },
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    };

    if (statusFilter) {
      // review and lapsed need to be date based
      switch (statusFilter) {
        case "review":
          filter.query.bool.must.push([
            {
              range: {
                review_date: {
                  gte: dayjs().unix(),
                  lte: dayjs()
                    .add(14, "day")
                    .unix(),
                },
              },
            },
            {
              term: {
                status: "approved",
              },
            },
          ]);
          break;
        case "lapsed":
          filter.query.bool.must.push([
            {
              range: {
                review_date: {
                  lte: dayjs().unix(),
                },
              },
            },
            {
              term: {
                status: "approved",
              },
            },
          ]);
          break;
        // everything else is status
        default:
          filter.query.bool.must.push({
            term: {
              status: statusFilter,
            },
          });
          break;
      }
    }

    if (state.filters.categories.length >= 1) {
      filter.query.bool.must.push({
        terms: {
          category: state.filters.categories,
        },
      });
    }

    if (tabFilter === "starred") {
      listFavourites = true;
    }

    /**
     * If there is a tab filter, and we are admin, get everything
     * Else retrict to what the user id is.
     * IF we are restricting by the user, then obviously it depends on the tab
     */
    if (state.user_filter) {
      const submittedByParam = ["pending", "rejected"];

      if (submittedByParam.includes(statusFilter)) {
        filter.query.bool.must.push({
          term: {
            created_user_id: state.user_filter,
          },
        });
      } else {
        filter.query.bool.must.push({
          bool: {
            should: [
              {
                term: {
                  primary_user_id: state.user_filter,
                },
              },
              {
                term: {
                  secondary_user_id: state.user_filter,
                },
              },
            ],
          },
        });
      }
    }

    const versionChecks = ["pending", "rejected", "lapsed", "review"];

    if (versionChecks.includes(statusFilter)) {
      return new Promise((resolve, reject) => {
        api()
          .put("/surveymanagement/versions", {
            filter,
          })
          .then((surveys) => {
            const returnDocs = surveys.data.data.version.map((doc) => ({
              ...doc,
              current_version: doc,
            }));
            commit("SET_FILTER_TOTAL", surveys.data.meta.filter_total);
            commit("SET_CURRENT_PAGE", page);
            commit("setSurveys", returnDocs);
            resolve(returnDocs);
          })
          .catch((error) => {
            commit("SET_FILTER_TOTAL", 0);
            commit("SET_CURRENT_PAGE", page);
            commit("setSurveys", []);
            resolve([]);
          })
          .finally(() => {
            setTimeout(() => {
              state.loading = false;
            }, 100);
          });
      });
    }
    let endpoint = `${state.apiEndpoint}s`;
    if (flat) {
      endpoint = `${endpoint}/flat`;
    }
    return new Promise((resolve, reject) => {
      api()
        .put(endpoint, { filter })
        .then((resp) => {
          commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
          commit("SET_CURRENT_PAGE", page);
          commit("setSurveys", resp.data.data.survey);
          resolve(resp.data.data.survey);
        })
        .catch((error) => {
          commit("SET_FILTER_TOTAL", 0);
          commit("SET_CURRENT_PAGE", page);
          commit("setSurveys", []);
          resolve([]);
        })
        .finally(() => {
          setTimeout(() => {
            state.loading = false;
          }, 100);
        });
    });
  },

  // get content, forms and policies, limit by 10 though and filter by name/title
  async listAllForSelect(context, filterTerm) {
    const returnArr = {};
    const limit = 5;

    // content first
    const contentQuery = {
      query: {
        bool: {
          must: [
            {
              terms: {
                status: ["approved", "pending"],
              },
            },
          ],
        },
      },
    };

    if (filterTerm) {
      contentQuery.query.bool.must.push({
        match_phrase_prefix: {
          title: {
            query: filterTerm,
            slop: 10,
          },
        },
      });
    }

    contentQuery.size = limit;
    contentQuery.from = 0;
    returnArr.content = null;
    try {
      const contentResults = await api().put("/surveymanagement/surveys/flat", {
        filter: contentQuery,
      });
      returnArr.content = contentResults.data.data.page;
    } catch (err) {
      delete returnArr.content;
    }

    // TODO: Currently we only want pages, so this needs to remove later
    return Promise.resolve(returnArr);
  },

  create({ state, dispatch }, documentData) {
    return new Promise((resolve, reject) => {
      api()
        .put(`${state.apiEndpoint}`, { survey: documentData })
        .then((form) => {
          dispatch(
            "notifications/create",
            {
              body: "The survey was created",
              type: "success",
            },
            { root: true }
          );

          resolve(form);
        })
        .catch((error) => {
          console.log(
            "TCL:  ~ file: actions.js ~ line 392 ~ returnnewPromise ~ error",
            error
          );
          dispatch(
            "notifications/create",
            {
              title: "Error!",
              body: "The survey failed to be created",
              type: "danger",
            },
            { root: true }
          );

          reject(error);
        });
    });
  },

  async read({ commit, state, dispatch }, { surveyId, versionId }) {
    commit("setCurrentSurvey", {});
    let form;

    try {
      form = await api().get(`${state.apiEndpoint}/${surveyId}`);
      if (versionId) {
        await dispatch("getVersion", { surveyId, versionId });
      }
      commit("setCurrentSurvey", form.data.data.survey[0]);

      if (form.data.data.survey[0].current_version && !versionId) {
        commit("SET_CURRENT_VERSION", form.data.data.survey[0].current_version);
      }

      return Promise.resolve(form.data.data.survey[0]);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  async retrieveByUrl({ commit }, url) {
    commit("setCurrentSurvey", {});

    let response;

    const filter = {
      filter: {
        query: {
          bool: {
            must: [
              {
                term: {
                  survey_url: url,
                },
              },
            ],
          },
        },
      },
    };

    try {
      response = await api().put("/surveymanagement/surveys", filter);
      commit("setCurrentSurvey", response.data.data.survey[0]);
      return Promise.resolve(response.data.data.survey[0]);
    } catch (err) {
      commit("setCurrentSurvey", {});
      return Promise.reject(new Error(err));
    }
  },

  delete({ state, dispatch }, surveyId) {
    return new Promise((resolve, reject) => {
      api()
        .delete(`${state.apiEndpoint}/${surveyId}`)
        .then((form) => {
          dispatch(
            "notifications/create",
            {
              body: "The form was deleted",
              type: "success",
            },
            { root: true }
          );

          dispatch("list");
          resolve(form);
        })
        .catch((error) => {
          dispatch(
            "notifications/create",
            {
              title: "Error!",
              body: "The form could not be deleted",
              type: "danger",
            },
            { root: true }
          );
          reject(error);
        });
    });
  },

  listVersions({ commit, state }, surveyId) {
    state.loadingDocVersions = true;
    return new Promise((resolve, reject) => {
      api()
        .get(`${state.apiEndpoint}/${surveyId}/versions/flat`)
        .then((versions) => {
          commit("addSurveyVersions", {
            surveyId,
            versions: versions.data.data.version,
          });
          resolve(versions);
          state.loadingDocVersions = false;
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  createVersion({ state, dispatch }, { surveyId, version, saveAsDraft }) {
    const { apiEndpoint } = state;

    const requestEndpoint = `${apiEndpoint}/${surveyId}/version`;
    const requestBody = {
      version,
      past_date_bypass: true,
      draft: saveAsDraft,
    };

    return new Promise((resolve, reject) => {
      api()
        .put(requestEndpoint, requestBody)
        .then((response) => {
          if (response.data.data) {
            const surveyVersion = response.data.data.version[0];
            resolve(surveyVersion);
            // Send notification
            dispatch(
              "notifications/create",
              {
                title: "Survey Version Created",
                body: "The survey version was created",
                type: "success",
              },
              { root: true }
            );
          } else {
            reject(response.data);
          }
        })
        .catch((error) => {
          if (error.response && hasProp(error.response.data.data, "version")) {
            const surveyVersion = error.response.data.data.version[0];
            resolve(surveyVersion);
            // Send notification
            dispatch(
              "notifications/create",
              {
                title: "Form Version Created",
                body: "The form version was created",
                type: "success",
              },
              { root: true }
            );
          } else {
            reject(error.response.data);
          }
        });
    });
  },

  updateVersion(
    { state, dispatch },
    { surveyId, versionId, version, saveAsDraft }
  ) {
    const { apiEndpoint } = state;

    const requestEndpoint = `${apiEndpoint}/${surveyId}/version/${versionId}`;
    const requestBody = {
      version,
      past_date_bypass: true,
      draft: saveAsDraft,
    };

    return new Promise((resolve, reject) => {
      api()
        .put(requestEndpoint, requestBody)
        .then((response) => {
          if (response.data.data) {
            const surveyVersion = response.data.data.version[0];
            resolve(surveyVersion);
            // Send notification
            dispatch(
              "notifications/create",
              {
                title: "Survey Version Updated",
                body: "The survey version was updated",
                type: "success",
              },
              { root: true }
            );
          } else {
            reject(response.data);
          }
        })
        .catch((error) => {
          if (error.response && hasProp(error.response.data.data, "version")) {
            const surveyVersion = error.response.data.data.version[0];
            // Send notification
            dispatch(
              "notifications/create",
              {
                title: "Form Version Updated",
                body: "The form version was updated",
                type: "success",
              },
              { root: true }
            );
            resolve(surveyVersion);
          } else {
            reject(error);
          }
        });
    });
  },

  approveForm({ state, dispatch }, { surveyId, versionId }) {
    const url = `${endpoint}/${surveyId}/version/${versionId}/approve`;
    return new Promise((resolve, reject) => {
      api()
        .put(url)
        .then((version) => {
          dispatch(
            "notifications/create",
            {
              body: "The form was approved",
              type: "success",
            },
            { root: true }
          );
          resolve(version);
        })
        .catch((error) => {
          dispatch(
            "notifications/create",
            {
              title: "Error!",
              body: "The form could not be approved",
              type: "danger",
            },
            { root: true }
          );
          reject(error);
        });
    });
  },

  // rejectDocument({ state, dispatch }, { documentId, versionId }) {
  //   return new Promise((resolve, reject) => {
  //     documentVersionsApi
  //       .reject(state.docType, documentId, versionId)
  //       .then((version) => {
  //         dispatch(
  //           'notifications/create',
  //           {
  //             body: `The ${state.docType.singular} was rejected`,
  //             type: 'success',
  //           },
  //           { root: true },
  //         );
  //         resolve(version);
  //       })
  //       .catch((error) => {
  //         dispatch(
  //           'notifications/create',
  //           {
  //             title: 'Error!',
  //             body: `The ${state.docType.singular} could not be rejected`,
  //             type: 'danger',
  //           },
  //           { root: true },
  //         );
  //         reject(error);
  //       });
  //   });
  // },

  async getVersion({ commit }, { surveyId, versionId }) {
    let resp;

    try {
      resp = await api().get(
        `/surveymanagement/survey/${surveyId}/version/${versionId}`
      );
      commit("SET_CURRENT_VERSION", resp.data.data.version[0]);
      return Promise.resolve(resp.data.data.version[0]);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  // favourite({ state, dispatch }, documentId) {
  //   documentsApi.favourite(state.docType, documentId).then((document) => {
  //     dispatch(
  //       'notifications/create',
  //       {
  //         body: `The ${state.docType.singular} <b>${document.current_version.title}</b> has been favorited`,
  //         type: 'success',
  //       },
  //       { root: true },
  //     );
  //   });
  // },

  // unfavourite({ state }, documentId) {
  //   documentsApi.unfavourite(state.docType, documentId);
  // },

  createAndPublish({ dispatch, rootGetters }, payload) {
    const documentData = payload.payload;
    const { saveAsDraft } = payload;
    const version = {
      title: documentData.current_version.title,
      version: documentData.current_version.version,
      elements: rootGetters["pageElements/forServer"] || null,
    };

    return new Promise((resolve, reject) => {
      dispatch("create", documentData)
        .then((document) => {
          dispatch("createVersion", {
            surveyId: document.data.data.survey[0].id,
            version,
            saveAsDraft,
          })
            .then((documentVersion) => {
              resolve({
                document,
                version: documentVersion,
              });
            })
            .catch((error) => reject(error));
        })
        .catch((error) => reject(error));
    });
  },

  /**
   * This is for just updating details about the document, not version
   */
  updateDocument({ state }, documentData) {
    const payload = {};
    payload[state.docType.singular] = documentData;

    return new Promise((resolve, reject) => {
      api()
        .post(`${state.apiEndpoint}/${documentData.id}/`, payload)
        .then((document) => {
          resolve(document);
        })
        .catch((error) => {
          console.log("error", { ...error });
          reject(error);
        });
    });
  },

  update({ state, dispatch, rootGetters }, document) {
    const documentData = document;
    const version = {
      title: documentData.pageData.current_version.title,
      version: documentData.version
        ? documentData.version
        : documentData.pageData.current_version.version,
      elements: rootGetters["pageElements/forServer"] || null,
    };

    let vAction = "updateVersion";
    let vPayload = {
      surveyId: documentData.surveyId,
      versionId: documentData.pageData.current_version.id,
      version,
      saveAsDraft: document.saveAsDraft,
    };

    /**
     * Tags. just need ids
     */
    if (documentData.pageData.tags) {
      documentData.pageData.tag_ids = documentData.pageData.tags.map(
        (tag) => tag.id
      );
    }

    /**
     * If we just updating, we need to go to updateVersion
     * If we have a new file and new version number, we need to go to createVersion
     */

    if (
      (documentData.pageData.current_version.orig_version &&
        documentData.pageData.current_version.orig_version !==
          version.version.toString()) ||
      !documentData.pageData.current_version.orig_version
    ) {
      vAction = "createVersion";
      vPayload = {
        surveyId: documentData.surveyId,
        version,
        saveAsDraft: document.saveAsDraft,
      };
    }

    const payload = documentData.pageData;

    return new Promise((resolve, reject) => {
      api()
        .post(`${state.apiEndpoint}/${documentData.surveyId}`, {
          survey: payload,
        })
        .then((document) => {
          dispatch(vAction, vPayload)
            .then((documentVersion) => {
              resolve({
                document,
                version: documentVersion,
              });
            })
            .catch((error) => reject(error));
        })
        .catch((error) => {
          console.log("error", { ...error });
          reject(error);
        });
    });
  },

  resubmit({ dispatch }, { surveyId, pageData, version }) {
    return new Promise((resolve, reject) => {
      dispatch("update", { surveyId, pageData, version })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          dispatch(
            "notifications/create",
            {
              body: "There was a problem saving the page.",
              type: "danger",
            },
            { root: true }
          );
          reject(err);
        });
    });
  },

  async getUserDocCount({ state, rootState, dispatch }) {
    let userId;

    if (!rootState.auth.currentUser.id) {
      await dispatch("auth/listCurrentUser", {}, { root: true }).then(
        (resp) => {
          userId = resp.id;
        }
      );
    } else {
      userId = rootState.auth.currentUser.id;
    }

    const payload = {
      filter: {
        query: {
          bool: {
            should: [
              {
                term: {
                  primary_user_id: userId,
                },
              },
              {
                term: {
                  created_user_id: userId,
                },
              },
              {
                term: {
                  secondary_user_id: userId,
                },
              },
            ],
          },
        },
      },
      size: 1,
      from: 0,
    };

    let response;

    try {
      response = await api().put(
        `/${state.docType.singular}management/${state.docType.plural}`,
        payload
      );
      return Promise.resolve(response.data.meta.filter_total);
    } catch (err) {
      return Promise.resolve(0);
    }
  },
};
